/**
 * Audio recording and playback service using Web Audio API
 */

export interface AudioRecorderOptions {
  mimeType?: string
  audioBitsPerSecond?: number
}

export interface RecordingResult {
  blob: Blob
  duration: number
  url: string
}

export class AudioRecorder {
  private mediaRecorder: MediaRecorder | null = null
  private audioChunks: Blob[] = []
  private stream: MediaStream | null = null
  private startTime: number = 0
  private audioContext: AudioContext | null = null
  private analyser: AnalyserNode | null = null
  private silenceDetectionInterval: number | null = null
  private onSilenceDetectedCallback: (() => void) | null = null

  /**
   * Initialize and request microphone permission
   */
  async initialize(): Promise<void> {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 48000,
        },
      })
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          throw new Error('Microphone permission denied')
        } else if (error.name === 'NotFoundError') {
          throw new Error('No microphone found')
        }
      }
      throw new Error('Failed to initialize audio recorder')
    }
  }

  /**
   * Start recording audio
   */
  async startRecording(options?: AudioRecorderOptions & { enableVAD?: boolean; onSilenceDetected?: () => void }): Promise<void> {
    if (!this.stream) {
      await this.initialize()
    }

    if (!this.stream) {
      throw new Error('No audio stream available')
    }

    this.audioChunks = []
    this.startTime = Date.now()

    // Determine best supported MIME type
    const mimeType =
      options?.mimeType ||
      this.getSupportedMimeType() ||
      'audio/webm;codecs=opus'

    this.mediaRecorder = new MediaRecorder(this.stream, {
      mimeType,
      audioBitsPerSecond: options?.audioBitsPerSecond || 128000,
    })

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.audioChunks.push(event.data)
      }
    }

    this.mediaRecorder.start(100) // Collect data every 100ms

    // Set up voice activity detection if enabled
    if (options?.enableVAD && options?.onSilenceDetected) {
      this.onSilenceDetectedCallback = options.onSilenceDetected
      this.setupVoiceActivityDetection()
    }
  }

  /**
   * Set up voice activity detection (VAD) to detect silence
   */
  private setupVoiceActivityDetection(): void {
    if (!this.stream) return

    try {
      // Create audio context and analyser
      this.audioContext = new AudioContext()
      this.analyser = this.audioContext.createAnalyser()
      this.analyser.fftSize = 2048
      this.analyser.smoothingTimeConstant = 0.8

      const source = this.audioContext.createMediaStreamSource(this.stream)
      source.connect(this.analyser)

      const dataArray = new Uint8Array(this.analyser.frequencyBinCount)
      let silenceStart: number | null = null
      let hasDetectedSpeech = false
      let speechStartTime: number | null = null
      const SILENCE_THRESHOLD = 20 // Volume threshold (0-255) - lowered for better microphone sensitivity
      const SILENCE_DURATION = 1500 // 1.5 seconds of silence
      const MIN_SPEECH_DURATION = 800 // Minimum 0.8 seconds of speech before detecting silence

      // Check audio level periodically
      this.silenceDetectionInterval = window.setInterval(() => {
        if (!this.analyser) return

        this.analyser.getByteFrequencyData(dataArray)

        // Calculate average volume
        const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length

        console.log('[VAD] Volume:', Math.round(average), 'Threshold:', SILENCE_THRESHOLD, 'HasSpeech:', hasDetectedSpeech)

        if (average > SILENCE_THRESHOLD) {
          // Sound detected
          silenceStart = null
          if (!hasDetectedSpeech && this.isRecording) {
            hasDetectedSpeech = true
            speechStartTime = Date.now()
            console.log('[VAD] Speech started')
          }
        } else if (hasDetectedSpeech && speechStartTime) {
          // Only detect silence if we've had enough speech time
          const speechDuration = Date.now() - speechStartTime
          if (speechDuration < MIN_SPEECH_DURATION) {
            // Not enough speech yet, ignore silence
            return
          }

          // Silence detected after speech
          if (silenceStart === null) {
            silenceStart = Date.now()
            console.log('[VAD] Silence started after', speechDuration, 'ms of speech')
          } else {
            const silenceDuration = Date.now() - silenceStart
            if (silenceDuration > SILENCE_DURATION) {
              // Silence lasted long enough - trigger callback
              console.log('[VAD] Silence threshold reached, triggering callback')
              if (this.onSilenceDetectedCallback && this.isRecording) {
                // Store callback reference and clear it BEFORE calling to prevent double-triggering
                const callback = this.onSilenceDetectedCallback
                this.onSilenceDetectedCallback = null

                // Clean up interval but keep recording active for callback
                if (this.silenceDetectionInterval) {
                  window.clearInterval(this.silenceDetectionInterval)
                  this.silenceDetectionInterval = null
                }

                callback()
              }
            }
          }
        }
      }, 100)
    } catch (error) {
      console.error('Failed to setup voice activity detection:', error)
    }
  }

  /**
   * Stop voice activity detection
   */
  private stopVoiceActivityDetection(): void {
    if (this.silenceDetectionInterval) {
      window.clearInterval(this.silenceDetectionInterval)
      this.silenceDetectionInterval = null
    }

    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }

    this.analyser = null
    this.onSilenceDetectedCallback = null
  }

  /**
   * Stop recording and return the audio blob
   */
  async stopRecording(): Promise<RecordingResult> {
    if (!this.mediaRecorder) {
      throw new Error('No active recording')
    }

    // Stop voice activity detection
    this.stopVoiceActivityDetection()

    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(new Error('No active recording'))
        return
      }

      this.mediaRecorder.onstop = () => {
        const duration = (Date.now() - this.startTime) / 1000
        const mimeType = this.mediaRecorder?.mimeType || 'audio/webm'
        const blob = new Blob(this.audioChunks, { type: mimeType })
        const url = URL.createObjectURL(blob)

        resolve({ blob, duration, url })
      }

      this.mediaRecorder.onerror = (event) => {
        reject(new Error(`Recording error: ${event}`))
      }

      this.mediaRecorder.stop()
    })
  }

  /**
   * Cancel recording without saving
   */
  cancelRecording(): void {
    this.stopVoiceActivityDetection()

    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop()
      this.audioChunks = []
    }
  }

  /**
   * Get recording state
   */
  get isRecording(): boolean {
    return this.mediaRecorder?.state === 'recording'
  }

  /**
   * Get recording duration in seconds
   */
  get currentDuration(): number {
    if (!this.isRecording) return 0
    return (Date.now() - this.startTime) / 1000
  }

  /**
   * Release resources
   */
  cleanup(): void {
    this.stopVoiceActivityDetection()

    if (this.mediaRecorder) {
      this.mediaRecorder.stop()
      this.mediaRecorder = null
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = null
    }

    this.audioChunks = []
  }

  /**
   * Get best supported MIME type for recording
   */
  private getSupportedMimeType(): string | null {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
    ]

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type
      }
    }

    return null
  }
}

/**
 * Audio playback utility
 */
export class AudioPlayer {
  private audio: HTMLAudioElement | null = null
  private playbackRate = 1

  /**
   * Play audio from URL or Blob
   */
  async play(source: string | Blob): Promise<void> {
    this.stop()

    const url = typeof source === 'string' ? source : URL.createObjectURL(source)

    this.audio = new Audio(url)
    this.audio.playbackRate = this.playbackRate
    this.audio.preload = 'auto'
    this.audio.currentTime = 0

    return new Promise((resolve, reject) => {
      if (!this.audio) {
        reject(new Error('Failed to create audio element'))
        return
      }

      const audio = this.audio
      let hasStarted = false
      let fallbackTimer: number | null = null

      const cleanup = () => {
        if (!audio) return
        audio.onended = null
        audio.onerror = null
        audio.removeEventListener('canplaythrough', handleCanPlay)
        if (fallbackTimer !== null) {
          window.clearTimeout(fallbackTimer)
        }
      }

      const startPlayback = () => {
        if (hasStarted || !audio) return
        hasStarted = true
        audio
          .play()
          .catch((err) => {
            cleanup()
            reject(err)
          })
      }

      const handleCanPlay = () => {
        startPlayback()
      }

      audio.onended = () => {
        cleanup()
        resolve()
      }

      audio.onerror = () => {
        cleanup()
        reject(new Error('Failed to play audio'))
      }

      if (audio.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
        startPlayback()
      } else {
        audio.addEventListener('canplaythrough', handleCanPlay, { once: true })
        fallbackTimer = window.setTimeout(startPlayback, 600)
      }
    })
  }

  /**
   * Stop current playback
   */
  stop(): void {
    if (this.audio) {
      this.audio.pause()
      this.audio.currentTime = 0
      this.audio = null
    }
  }

  /**
   * Adjust playback speed for current and future audio
   */
  setPlaybackRate(rate: number): void {
    this.playbackRate = rate
    if (this.audio) {
      this.audio.playbackRate = rate
    }
  }

  /**
   * Check if audio is playing
   */
  get isPlaying(): boolean {
    return this.audio ? !this.audio.paused : false
  }
}

/**
 * Check if audio recording is supported
 */
export function isAudioRecordingSupported(): boolean {
  return !!(
    navigator.mediaDevices &&
    navigator.mediaDevices.getUserMedia &&
    window.MediaRecorder
  )
}

/**
 * Check if microphone permission is granted
 */
export async function checkMicrophonePermission(): Promise<PermissionState> {
  try {
    const permission = await navigator.permissions.query({ name: 'microphone' as PermissionName })
    return permission.state
  } catch {
    // Fallback for browsers that don't support permissions query
    return 'prompt'
  }
}
